//src/dashboard/EmailLeadSettings.jsx

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { AuthContext } from '../context/AuthContext';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from '../config/firebaseConfig';
import { toast } from 'react-toastify';
import CircularProgress from '@mui/material/CircularProgress';

export default function EmailLeadSettings() {
  const intl = useIntl();
  const { currentUser } = useContext(AuthContext);

  // Fallback auth (guards SSR and delayed auth resolution)
  const [authUid, setAuthUid] = useState(getAuth().currentUser?.uid || null);
  useEffect(() => onAuthStateChanged(getAuth(), (u) => setAuthUid(u?.uid || null)), []);

  const uid = useMemo(
    () => currentUser?.uid || currentUser?.id || authUid || null,
    [currentUser, authUid]
  );

  const forwardingAddress = useMemo(
    () => (uid ? `leads+${uid}@lugarai.com` : ''),
    [uid]
  );

  const settingsDocRef = useMemo(
    () => (uid ? doc(db, 'users', uid, 'settings', 'email_ingestion') : null),
    [uid]
  );

  const [enabled, setEnabled] = useState(false);
  const [lastReceived, setLastReceived] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingToggle, setSavingToggle] = useState(false);

  const [sources, setSources] = useState([]); // [{id, portalName, portalEmail}]
  const [portalName, setPortalName] = useState('');
  const [portalEmail, setPortalEmail] = useState('');
  const [copied, setCopied] = useState(false);

  const disabledNoAuth = !uid;

  const emailIsValid = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim());

  const toDisplayDate = (val) => {
    try {
      if (!val) return null;
      if (typeof val === 'object' && val.seconds) {
        const d = new Date(val.seconds * 1000);
        return d.toLocaleString();
      }
      const d = new Date(val);
      if (isNaN(d.getTime())) return String(val);
      return d.toLocaleString();
    } catch {
      return String(val);
    }
  };

  // Load settings
  useEffect(() => {
    const load = async () => {
      if (!settingsDocRef) {
        setLoading(false);
        return;
      }
      try {
        const snap = await getDoc(settingsDocRef);
        if (snap.exists()) {
          const data = snap.data() || {};
          if (typeof data.enabled === 'boolean') setEnabled(data.enabled);
          if (data.last_received) setLastReceived(toDisplayDate(data.last_received));
          if (Array.isArray(data.sources)) setSources(data.sources);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [settingsDocRef]);

  // Actions
  const handleCopy = async () => {
    if (!forwardingAddress) return;
    await navigator.clipboard.writeText(forwardingAddress);
    setCopied(true);
    toast.success(intl.formatMessage({ id: 'emailLeadSettings.copied' }));
    setTimeout(() => setCopied(false), 1200);
  };

  const handleToggle = async () => {
    if (!settingsDocRef) return;
    try {
      setSavingToggle(true);
      await setDoc(settingsDocRef, { enabled: !enabled }, { merge: true });
      setEnabled((prev) => !prev);
    } finally {
      setSavingToggle(false);
    }
  };

  const handleAddSource = async () => {
    if (!settingsDocRef) return;
    const name = portalName.trim();
    const email = portalEmail.trim().toLowerCase();

    if (!name) {
      toast.error(intl.formatMessage({ id: 'fieldRequired' }));
      return;
    }
    if (!emailIsValid(email)) {
      toast.error(intl.formatMessage({ id: 'invalidEmail' }));
      return;
    }
    if (sources.some((s) => (s.portalEmail || '').toLowerCase() === email)) {
      toast.error(intl.formatMessage({ id: 'chatbotSettings.saveError' }));
      return;
    }

    const newSources = [
      ...sources,
      { id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, portalName: name, portalEmail: email },
    ];
    await setDoc(settingsDocRef, { sources: newSources }, { merge: true });
    setSources(newSources);
    setPortalName('');
    setPortalEmail('');
    toast.success(intl.formatMessage({ id: 'emailLeadSettings.sourcesSaved' }));
  };

  const handleRemoveSource = async (id) => {
    if (!settingsDocRef) return;
    const newSources = sources.filter((s) => s.id !== id);
    await setDoc(settingsDocRef, { sources: newSources }, { merge: true });
    setSources(newSources);
    toast.success(intl.formatMessage({ id: 'emailLeadSettings.sourcesSaved' }));
  };

  if (loading) {
    return (
      <div className="la loading-row">
        <CircularProgress size={20} />
        <span>{intl.formatMessage({ id: 'auth.userNotLoaded' })}</span>
      </div>
    );
  }

  return (
    <div className="la">
      <div className="dashboard-content">
        {/* Title */}
        <h1 className="page-title">{intl.formatMessage({ id: 'emailLeadSettings.title' })}</h1>

        {/* Intro / Steps */}
        <section className="card card-dashed mb-16">
          <p className="subtitle-muted mb-0">
            {intl.formatMessage({ id: 'emailLeadSettings.introText' })}
          </p>
          <ol className="ol-steps mt-16">
            <li>{intl.formatMessage({ id: 'emailLeadSettings.step1' })}</li>
            <li>{intl.formatMessage({ id: 'emailLeadSettings.step2' })}</li>
            <li>{intl.formatMessage({ id: 'emailLeadSettings.step3' })}</li>
          </ol>
        </section>

        {/* Forwarding Address */}
        <section className="card mb-16">
          <div className="section-title">
            {intl.formatMessage({ id: 'emailLeadSettings.forwardingTitle' })}
          </div>
          <div className="copy-input-wrapper mt-16">
            <input
              className="input-field copy-input"
              readOnly
              disabled={!forwardingAddress}
              value={
                forwardingAddress ||
                intl.formatMessage({ id: 'emailLeadSettings.loginToSeeAddress' })
              }
            />
            <button
              type="button"
              onClick={handleCopy}
              disabled={!forwardingAddress}
              className="copy-button"
            >
              {copied
                ? intl.formatMessage({ id: 'emailLeadSettings.copied' })
                : intl.formatMessage({ id: 'emailLeadSettings.copy' })}
            </button>
          </div>
          <p className="status-subtext">
            {intl.formatMessage({ id: 'emailLeadSettings.forwardingNote' })}
          </p>
        </section>

        {/* Sources */}
        <section className="card mb-16">
          <div className="section-title">
            {intl.formatMessage({ id: 'emailLeadSettings.sourceTitle' })}
          </div>

          {sources.length === 0 ? (
            <div className="status-box mt-16">
              {intl.formatMessage({ id: 'emailLeadSettings.noSources' })}
            </div>
          ) : (
            <div className="table-shell mt-16">
              <div className="table-head-row">
                <div>Portal</div>
                <div>E-mail</div>
                <div className="text-right">{intl.formatMessage({ id: 'user.actions' })}</div>
              </div>
              {sources.map((s) => (
                <div key={s.id} className="table-row">
                  <div className="break-anywhere">{s.portalName}</div>
                  <div className="muted break-anywhere">{s.portalEmail}</div>
                  <div className="text-right">
                    <button
                      type="button"
                      onClick={() => handleRemoveSource(s.id)}
                      disabled={disabledNoAuth}
                      className="danger-button"
                      title={intl.formatMessage({ id: 'general.delete' })}
                    >
                      {intl.formatMessage({ id: 'general.delete' })}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add row */}
          <div className="grid-add-row mt-16">
            <input
              type="text"
              className="input-field"
              placeholder={intl.formatMessage({ id: 'emailLeadSettings.portalNamePlaceholder' })}
              value={portalName}
              onChange={(e) => setPortalName(e.target.value)}
              disabled={disabledNoAuth}
            />
            <input
              type="email"
              className="input-field"
              placeholder={intl.formatMessage({ id: 'emailLeadSettings.portalEmailPlaceholder' })}
              value={portalEmail}
              onChange={(e) => setPortalEmail(e.target.value)}
              disabled={disabledNoAuth}
            />
            <button
              type="button"
              onClick={handleAddSource}
              disabled={
                disabledNoAuth ||
                !portalName.trim() ||
                !emailIsValid(portalEmail.trim().toLowerCase())
              }
              className="primary-button"
            >
              {intl.formatMessage({ id: 'emailLeadSettings.addSource' })}
            </button>
          </div>
        </section>

        {/* Status & Toggle */}
        <section className={`status-card ${enabled ? 'active' : ''}`}>
          <div className="form-grid two-cols">
            <div>
              <div className="status-title">
                {enabled
                  ? `🟢 ${intl.formatMessage({ id: 'emailLeadSettings.status.active' })}`
                  : `🔴 ${intl.formatMessage({ id: 'emailLeadSettings.status.inactive' })}`}
              </div>
              {enabled && lastReceived && (
                <div>
                  {intl.formatMessage(
                    { id: 'emailLeadSettings.status.lastReceived' },
                    { lastReceived }
                  )}
                </div>
              )}
              {!enabled && (
                <div className="status-subtext">
                  {intl.formatMessage({ id: 'emailLeadSettings.status.inactiveDetails' })}
                </div>
              )}
            </div>

            <div className="row center gap-10">
              <input
                id="toggle-email-ingestion"
                type="checkbox"
                checked={enabled}
                onChange={handleToggle}
                disabled={savingToggle || disabledNoAuth}
              />
              <label htmlFor="toggle-email-ingestion" style={{ userSelect:'none' }}>
                {intl.formatMessage({ id: 'emailLeadSettings.toggle.label' })}
              </label>
              {savingToggle && <CircularProgress size={16} />}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
